import React from "react";
import { Moon, Sun, Activity, Cpu, Clock, AlertTriangle } from "lucide-react";
import { useApp } from "../context/AppContext";

function Pill({ icon: Icon, label, value, ok, testid }) {
  const color = ok ? "var(--accent)" : "var(--danger)";
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--bd)] bg-[var(--surface2)]"
      data-testid={testid}
    >
      <span className="w-2 h-2 rounded-full pulse-dot" style={{ background: color }} />
      <Icon size={13} className="text-[var(--t2)]" />
      <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--t2)] hidden sm:inline">{label}</span>
      <span className="font-mono text-xs font-medium" style={{ color }}>{value}</span>
    </div>
  );
}

export function Header() {
  const { theme, toggleTheme, mqttConnected, telemetry, lastUpdate } = useApp();
  const updated = lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[var(--bg)]/80 border-b border-[var(--bd)]" data-testid="app-header">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-heading font-black text-xl sm:text-2xl tracking-tighter flex items-center gap-2">
            <span className="text-[var(--accent)]">⚡</span>
            <span className="truncate">Campus Energy Monitor</span>
          </h1>
          <p className="text-[10px] sm:text-xs text-[var(--t2)] tracking-wide truncate">
            Cloud Dashboard · Cloudflare Pages · CPUT Final Year Project
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:flex items-center gap-2">
            <Pill icon={Activity} label="MQTT" value={mqttConnected ? "LIVE" : "OFFLINE"} ok={mqttConnected} testid="pill-mqtt" />
            <Pill icon={Cpu} label="ESP32" value={telemetry?.online ? "ONLINE" : "WAIT"} ok={!!telemetry?.online} testid="pill-esp32" />
            <Pill icon={Clock} label="Update" value={updated} ok={mqttConnected} testid="pill-update" />
          </div>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="w-10 h-10 rounded-md border border-[var(--bd)] bg-[var(--surface2)] flex items-center justify-center transition-colors hover:border-[var(--bd-strong)] active:scale-95"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>

      {/* mobile pills */}
      <div className="md:hidden flex gap-2 px-4 pb-3 overflow-x-auto hide-scrollbar">
        <Pill icon={Activity} label="MQTT" value={mqttConnected ? "LIVE" : "OFFLINE"} ok={mqttConnected} testid="pill-mqtt-m" />
        <Pill icon={Cpu} label="ESP32" value={telemetry?.online ? "ONLINE" : "WAIT"} ok={!!telemetry?.online} testid="pill-esp32-m" />
        <Pill icon={Clock} label="Update" value={updated} ok={mqttConnected} testid="pill-update-m" />
      </div>

      {!mqttConnected && (
        <div className="bg-[var(--warning-bg)] text-black overflow-hidden" data-testid="mqtt-warning-banner">
          <div className="marquee-track py-1.5">
            {[0, 1].map((k) => (
              <span key={k} className="font-mono text-xs font-bold tracking-wide px-4 flex items-center gap-2">
                <AlertTriangle size={13} /> MQTT BROKER DISCONNECTED — ATTEMPTING RECONNECT TO HIVEMQ CLOUD · SENSOR DATA UNAVAILABLE · STANDBY MODE
              </span>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
